import { useEffect, useState } from 'react'
import { Loader2, PenLine } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { toast } from '../../lib/shadcn/sonner'
import { useGetEmailSignature, useUpdateEmailSignature, useUploadEmailImage } from '../../hooks/backend/crm'
import RichTextEditor from '../../components/RichTextEditor'

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export default function EmailSignatureSettings() {
  const getSignature = useGetEmailSignature()
  const updateSignature = useUpdateEmailSignature()
  const uploadEmailImage = useUploadEmailImage()

  const [signature, setSignature] = useState('')
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    void getSignature.trigger({}).then((data: unknown) => {
      setSignature((data as { signature?: string } | undefined)?.signature ?? '')
      setLoaded(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleImageUpload = async (file: File): Promise<string | null> => {
    try {
      const base64 = await fileToBase64(file)
      const res = (await uploadEmailImage.trigger({
        fileName: file.name,
        data: base64,
        mimeType: file.type,
      })) as { url?: string } | undefined
      if (!res?.url) {
        toast.error('Image upload failed')
        return null
      }
      return res.url
    } catch {
      toast.error('Image upload failed')
      return null
    }
  }

  const handleSave = async () => {
    try {
      await updateSignature.trigger({ signature })
      toast.success('Signature saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save signature')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PenLine className="h-4 w-4 text-muted-foreground" />
          Email Signature
        </CardTitle>
        <CardDescription>
          Create your signature, then click &quot;Insert signature&quot; when composing an email.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!loaded ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            <RichTextEditor
              value={signature}
              onChange={setSignature}
              onImageUpload={handleImageUpload}
              placeholder="e.g. Sam Jones — Poppin' West Texas · (806) 555-0134"
            />
            <div className="flex justify-end">
              <Button onClick={() => void handleSave()} disabled={updateSignature.loading} className="gap-1.5">
                {updateSignature.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save Signature
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
